import { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import IntegrationsOverview from './content/IntegrationsOverview';
import QuickStart from './content/QuickStart';
import InstallPabs from './content/InstallPabs';
import WhatIsPabs from './content/WhatIsPabs';
import DataPrivacySecurity from './content/DataPrivacySecurity';
import DocsPromptBlock from './DocsPromptBlock';
import DocsFeatureGuide from './DocsFeatureGuide';
import { featureGuides } from './featureGuides';
import DocsWorkflowVisual from './DocsWorkflowVisual';
import DocsDesignAuthorityVisual from './DocsDesignAuthorityVisual';

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

export function extractHeadings(markdown) {
  const lines = markdown.split('\n');
  const headings = [];
  lines.forEach((line) => {
    const h2 = line.match(/^## (.+)/);
    const h3 = line.match(/^### (.+)/);
    if (h2) headings.push({ text: h2[1].trim(), level: 2, id: slugify(h2[1].trim()) });
    else if (h3) headings.push({ text: h3[1].trim(), level: 3, id: slugify(h3[1].trim()) });
  });
  return headings;
}

const components = {
  h1: ({ children }) => (
    <h1
      id={slugify(String(children))}
      className="text-3xl font-bold tracking-tighter mt-0 mb-6 leading-tight text-gray-900 dark:text-brand-50"
    >
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2
      id={slugify(String(children))}
      className="text-xl font-semibold tracking-tight mt-10 mb-4 leading-snug scroll-mt-20 text-gray-900 dark:text-brand-50"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3
      id={slugify(String(children))}
      className="text-[15px] font-semibold mt-7 mb-3 leading-snug scroll-mt-20 text-gray-700 dark:text-brand-200"
    >
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="leading-relaxed mb-4 text-[15px] text-gray-600 dark:text-brand-400">
      {children}
    </p>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="underline underline-offset-2 transition-colors text-gray-700 decoration-gray-300 hover:text-gray-900 hover:decoration-gray-600 dark:text-brand-300 dark:decoration-brand-700 dark:hover:text-brand-50 dark:hover:decoration-brand-400"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-5 mb-4 space-y-1.5 text-[15px] text-gray-600 dark:text-brand-400">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-5 mb-4 space-y-1.5 text-[15px] text-gray-600 dark:text-brand-400">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  pre: ({ children }) => <DocsPromptBlock>{children}</DocsPromptBlock>,
  code: ({ inline, className, children }) => {
    if (!inline && (className || String(children).endsWith('\n'))) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code className="px-1.5 py-0.5 rounded text-[13px] font-mono bg-gray-100 text-gray-700 border border-gray-200 dark:bg-brand-800 dark:text-brand-300 dark:border-brand-50/10">
        {children}
      </code>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 pl-4 my-4 italic text-[15px] border-gray-300 text-gray-500 dark:border-brand-600 dark:text-brand-500">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-6">
      <table className="w-full text-[14px] text-left border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-gray-200 dark:border-brand-50/10">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-gray-100 dark:divide-brand-50/5">{children}</tbody>
  ),
  th: ({ children }) => (
    <th className="py-2.5 pr-6 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-brand-600">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="py-2.5 pr-6 align-top text-gray-600 dark:text-brand-400">{children}</td>
  ),
  hr: () => <hr className="my-8 border-gray-200 dark:border-brand-50/10" />,
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-800 dark:text-brand-200">{children}</strong>
  ),
};

// Slugs that render custom React components instead of markdown
const customPages = {
  'integrations-overview': IntegrationsOverview,
  'create-your-first-plan': QuickStart,
  'install-pabs': InstallPabs,
  'what-is-pabs': WhatIsPabs,
  'data-privacy-security': DataPrivacySecurity,
};

export default function DocsContent({ slug, onHeadingsExtracted }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const CustomComponent = customPages[slug];

  useEffect(() => {
    if (!slug || customPages[slug]) return;
    setLoading(true);
    setError(false);

    import(`./content/${slug}.md`)
      .then((module) => {
        fetch(module.default)
          .then((r) => r.text())
          .then((text) => {
            setContent(text);
            if (onHeadingsExtracted) onHeadingsExtracted(extractHeadings(text));
            setLoading(false);
          })
          .catch(() => { setError(true); setLoading(false); });
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug, onHeadingsExtracted]);

  // Custom component pages bypass markdown loading
  if (CustomComponent) {
    return <CustomComponent onHeadingsExtracted={onHeadingsExtracted} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="w-5 h-5 border-2 rounded-full animate-spin border-gray-200 border-t-gray-500 dark:border-brand-700 dark:border-t-brand-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-400 dark:text-brand-600">Page not found.</p>
      </div>
    );
  }

  const workflowStage = {
    'workflow-shape': 'shape',
    'workflow-decide': 'decide',
    'workflow-build': 'build',
    'workflow-check': 'check',
    'workflow-maintain': 'maintain',
  }[slug];

  if (workflowStage) {
    const workflowMatch = content.match(/^#\s+(.+)\n+([\s\S]*?)(?=\n##\s)/);
    const title = workflowMatch?.[1] || allTitle(content);
    const introduction = workflowMatch?.[2]?.trim() || '';
    const detail = workflowMatch ? content.slice(workflowMatch[0].length).trim() : content.replace(/^#\s+.+\n*/, '');

    return (
      <article className="min-w-0 max-w-[760px]">
        <h1 className="text-3xl font-bold tracking-tighter mt-0 mb-6 leading-tight text-gray-900 dark:text-brand-50">
          {title}
        </h1>
        {introduction && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{introduction}</ReactMarkdown>
        )}
        <DocsWorkflowVisual active={workflowStage} />
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{detail}</ReactMarkdown>
      </article>
    );
  }

  const featureGuide = featureGuides[slug];
  if (featureGuide) {
    const featureMatch = content.match(/^#\s+(.+)\n+([\s\S]*?)(?=\n##\s)/);
    const title = featureMatch?.[1] || allTitle(content);
    const introduction = featureMatch?.[2]?.trim() || '';
    const detail = featureMatch ? content.slice(featureMatch[0].length).trim() : content.replace(/^#\s+.+\n*/, '');
    const setupMatch = slug === 'design-authority'
      ? detail.match(/^## Set it up for a plan\n[\s\S]*?(?=\n##\s)/)
      : null;
    const setupSection = setupMatch?.[0]?.trim() || '';
    const remainingDetail = setupMatch
      ? detail.slice(setupMatch[0].length).trim()
      : detail;

    if (featureGuide.presentation === 'reference') {
      return (
        <article className="min-w-0 max-w-[760px]">
          <h1 className="text-3xl font-bold tracking-tighter mt-0 mb-6 leading-tight text-gray-900 dark:text-brand-50">
            {title}
          </h1>
          {introduction && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{introduction}</ReactMarkdown>
          )}
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{detail}</ReactMarkdown>
        </article>
      );
    }

    return (
      <article className="min-w-0 max-w-[760px]">
        <h1 className="text-3xl font-bold tracking-tighter mt-0 mb-6 leading-tight text-gray-900 dark:text-brand-50">
          {title}
        </h1>
        {introduction && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{introduction}</ReactMarkdown>
        )}
        {slug === 'design-authority' && <DocsDesignAuthorityVisual />}
        <DocsFeatureGuide
          guide={featureGuide}
          interstitial={setupSection ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{setupSection}</ReactMarkdown>
          ) : null}
          contentBeforeUsage={slug !== 'design-authority' ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{remainingDetail}</ReactMarkdown>
          ) : null}
        />
        {slug === 'design-authority' && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{remainingDetail}</ReactMarkdown>
        )}
      </article>
    );
  }

  return (
    <article className="min-w-0 max-w-[680px]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{content}</ReactMarkdown>
    </article>
  );
}

function allTitle(markdown) {
  return markdown.match(/^#\s+(.+)/)?.[1] || 'Documentation';
}
